import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { StoreProvider } from '../contexts/StoreContext';
import { useContentStore } from '../contexts/StoreContext';
import { useBundledContent } from '../hooks/useBundledContent';
import { LocalStorageAdapter } from '../storage/LocalStorageAdapter';
import type { NavigationTab, ProGroupsProps } from '../types/navigation';
import { ProGroupsShell } from '../components/ProGroupsShell';
import { ReportView } from '../components/views/ReportView';
import type { ViewReportParams } from '../components/views/clients';
import type { AdminAssessmentDto } from '../types/adminAssessment';
import type { AdminResponseDto } from '../types/AdminResponseDto';
import { generateFullReportHtml, parseTrendChartPreloads, buildTrendChartOverrides } from '../utils/reportRenderer';
import { resolveTrendHistoryFromEmbedded } from '../services/clients/ClientReportAssessments';
import { getPhiValue, PHI_FIELD } from '../types/api/patinfo';
import type { PatinfoProfileDto, PatinfoHistoryDto } from '../types/api/patinfo';
import type { AppProfilePHIDto, MVarDataDto } from '../types/api/appProfile';
import { ProGroupsUiStateStorage, resolveProGroupsUiStateUserKey } from './progroups-shell/ProGroupsUiStateStorage';
import { WebClientCacheStorageAdapter } from './progroups-shell/WebClientCacheStorageAdapter';
import { openReportAsPdf } from './reportPdf';
import { resolveAccountSkinTypeWithRetry } from '../services/auth/resolveAccountId';

interface InlineReportViewState {
  profileId: string;
  adminResponse: AdminResponseDto;
  assessmentDefinition: AdminAssessmentDto;
  profilePHI: AppProfilePHIDto;
  /** Narrows ReportView to just this one report (AdminAssessmentReportDto.reportId) — without
   * it every report/section in the assessment renders, which is what made viewing one report
   * from the Clients Reports list incorrectly show everything once switched to ReportView. */
  selectedReportId: string;
  /** The profile's full embedded history — passed straight through to ReportView's
   * assessmentHistory prop so it can precompute enableBarGraph/enableLineGraph trend chart data. */
  assessmentHistory: PatinfoHistoryDto[];
}

// The six fields getPHI() reads directly off AppProfilePHIDto (see reportRenderer.ts) —
// everything else on the profile's PHI list is exposed through .extra instead, where getPHI()
// falls back to a case-insensitive name lookup.
const DIRECT_PHI_FIELD_NAMES = new Set<string>([
  PHI_FIELD.FNAME, PHI_FIELD.LNAME, PHI_FIELD.EMAIL, PHI_FIELD.DOB, PHI_FIELD.GENDER,
]);

/**
 * Builds report-script PHI from the client profile record itself — used when the completed
 * administration has no PHI snapshot of its own (adminResponse.phi, captured off
 * AdminStateDto.phi at completion time; blank for responses submitted before that field
 * existed, or for any assessment that never collected PHI mid-session). Pulls both:
 *  - the standard search/header row identity fields (fname/lname/patid/dob — same precedence
 *    ProfileRowEditor's own edit form uses, see ClientFormBuilder.ts's profileToClientEditForm)
 *  - whatever "client information" fields the account has enabled for management (Account >
 *    Settings > Client Info — arbitrary MVarDto-named fields, e.g. gender, phone, address),
 *    which live in the same profile.phi list under their own field name
 * so a report generated from bare profile data (no administration-time PHI) still has as much
 * identifying/demographic context as the Clients tab itself shows for this profile.
 */
const buildPhiFromProfile = (profile: PatinfoProfileDto): AppProfilePHIDto => {
  const phi = profile.phi ?? [];
  const fname = getPhiValue(phi, PHI_FIELD.FNAME);
  const lname = getPhiValue(phi, PHI_FIELD.LNAME);
  // Same precedence as ClientFormBuilder.ts's profileToClientEditForm: the PHI list's own
  // 'patid' entry wins, falling back to the profile's top-level patientid/patcode identifiers.
  const patid = getPhiValue(phi, PHI_FIELD.PATID) || profile.patientid || profile.patcode;

  const extra: MVarDataDto[] = phi
    .filter((p) => p.name && !DIRECT_PHI_FIELD_NAMES.has(p.name.toLowerCase()))
    .map((p) => ({ name: p.name, value: p.value }));
  if (patid && !extra.some((e) => e.name.toLowerCase() === PHI_FIELD.PATID)) {
    extra.push({ name: PHI_FIELD.PATID, value: patid });
  }

  return {
    dname: [fname, lname].filter(Boolean).join(' '),
    fname,
    lname,
    email: getPhiValue(phi, PHI_FIELD.EMAIL),
    dob: getPhiValue(phi, PHI_FIELD.DOB),
    gender: getPhiValue(phi, PHI_FIELD.GENDER),
    extra,
  };
};

export const ProGroupsWeb: React.FC<ProGroupsProps> = ({
  initialTab = 'Clients',
  apiService,
  storage: storageProp,
  onSignOut,
}) => {
  const storage = useMemo(() => storageProp ?? new LocalStorageAdapter(), [storageProp]);

  return (
    <StoreProvider storage={storage} apiService={apiService}>
      <ProGroupsWebSharedShell
        initialTab={initialTab}
        apiService={apiService}
        storage={storage}
        onSignOut={onSignOut}
      />
    </StoreProvider>
  );
};

const ProGroupsWebSharedShell: React.FC<{
  initialTab: NavigationTab;
  apiService: any;
  storage: any;
  onSignOut?: () => void;
}> = ({ initialTab, apiService, storage, onSignOut }) => {
  const contentStore = useContentStore();
  const stableContentStore = useMemo(() => contentStore, [contentStore]);
  useBundledContent(
    storage,
    stableContentStore,
    !!storage && !!stableContentStore
  );

  const uiStateStorage = useMemo(
    () => new ProGroupsUiStateStorage(storage, resolveProGroupsUiStateUserKey()),
    [storage]
  );
  const clientCacheStorage = useMemo(() => new WebClientCacheStorageAdapter(uiStateStorage), [uiStateStorage]);

  // Clients "view report" (Reports table's two View-column icons — see ProfileRowEditor):
  // the 👁 inline-view icon switches this tab to the full ReportView UI showing just the
  // selected report (with a Back button returning to Clients); the 📄 PDF icon generates the
  // report as a PDF in a new browser tab and leaves this tab's view completely unchanged.
  // Previously a single icon did both (PDF always, ReportView too when a debug flag was on) —
  // split per two review rounds: it was overkill, and the ReportView switch wasn't narrowed to
  // the one selected report, so it incorrectly showed every report/section in the assessment.
  const [inlineReportView, setInlineReportView] = useState<InlineReportViewState | null>(null);

  // The active account's own SkinTypeDef (e.g. PsychScan vs. ChildNav) — ReportView takes a
  // skinType prop and falls back to APP_CONSTANTS.DEFAULT_SKIN_TYPE ('2' = ChildNav) when it's
  // omitted, which is exactly what was happening here: this inline view render didn't pass one
  // at all, so every account's report showed ChildNav's header instead of its own.
  //
  // This has regressed several times because the two things it depends on both land
  // asynchronously on a cold load and there is no single reliable "ready" event: the userStore
  // session (holds the accountId) and the AccountPracInfoStorage cache (holds SkinType) — and
  // on a cache-wiped start that lands on the Clients tab, nothing even *fetches* the latter.
  // resolveAccountSkinTypeWithRetry polls both and, given apiService, fetches AccountSettings
  // itself if the cache stays cold — see its doc comment.
  const [accountSkinType, setAccountSkinType] = useState<string | undefined>(undefined);
  useEffect(() => {
    let cancelled = false;
    let runToken = 0;

    const resolveSkinType = async (maxAttempts?: number) => {
      const myToken = ++runToken;
      const skinType = await resolveAccountSkinTypeWithRetry(storage, {
        shouldCancel: () => cancelled || myToken !== runToken,
        maxAttempts,
        apiService,
      });
      if (!cancelled && myToken === runToken && skinType) {
        setAccountSkinType(skinType);
      }
    };

    void resolveSkinType();
    // A switch/data-loaded event means the data is present now — a quick re-check, not the full poll.
    const handleAccountChanged = () => { void resolveSkinType(4); };
    window.addEventListener('progroups:active-account-changed', handleAccountChanged);
    window.addEventListener('progroups:account-data-loaded', handleAccountChanged);
    return () => {
      cancelled = true;
      window.removeEventListener('progroups:active-account-changed', handleAccountChanged);
      window.removeEventListener('progroups:account-data-loaded', handleAccountChanged);
    };
  }, [storage, apiService]);

  const handleViewReport = useCallback((params: ViewReportParams) => {
    if (!params.selectedReport || !params.assessmentDefinition) {
      console.warn('ProGroupsWeb: onViewReport called with no resolvable report — nothing to show', {
        recId: params.adminResponse.recId,
        recAssessment: params.adminResponse.recAssessment,
      });
      return;
    }
    // params.adminResponse's own PHI snapshot (captured off AdminStateDto.phi when that
    // administration completed) wins over the client profile's current live PHI, matching the
    // precedence ReportView.tsx uses — see its effectivePhi. Falls back to building PHI from the
    // profile record itself for responses saved before that field existed (see
    // buildPhiFromProfile's doc comment for exactly what that pulls in).
    const phi = params.adminResponse.phi ?? buildPhiFromProfile(params.profile);

    if (params.mode === 'inline') {
      setInlineReportView({
        profileId: params.profile.patcode || params.profile.patientid || '',
        adminResponse: params.adminResponse,
        assessmentDefinition: params.assessmentDefinition,
        profilePHI: phi,
        selectedReportId: params.selectedReport.reportId,
        assessmentHistory: params.profile.history ?? [],
      });
      return;
    }

    // mode omitted or 'pdf' — PDF only, current tab/view untouched. Precompute trend chart data
    // (enableBarGraph/enableLineGraph) the same way the inline view does (see ReportView's
    // handleGenerateFullReport) so the PDF export of a report doesn't silently omit trend charts
    // the inline view shows for the same report/profile.
    const dataOverrides = new Map<string, string>();
    const trendRequests = parseTrendChartPreloads(params.selectedReport.reportPreloads);
    if (trendRequests.barGraphs.length > 0 || trendRequests.lineGraphs.length > 0) {
      const trendHistory = resolveTrendHistoryFromEmbedded(params.adminResponse, params.profile.history);
      buildTrendChartOverrides(trendRequests, trendHistory).forEach((v, k) => dataOverrides.set(k, v));
    }
    const html = generateFullReportHtml(params.selectedReport, params.adminResponse, phi, {}, dataOverrides);
    void openReportAsPdf(html, params.assessmentDefinition.dName || 'Report');
  }, []);

  if (inlineReportView) {
    return (
      <ReportView
        profileId={inlineReportView.profileId}
        initialAdminResponse={inlineReportView.adminResponse}
        assessmentDefinition={inlineReportView.assessmentDefinition}
        selectedReportId={inlineReportView.selectedReportId}
        assessmentHistory={inlineReportView.assessmentHistory}
        storage={storage}
        apiService={apiService}
        profilePHI={inlineReportView.profilePHI}
        skinType={accountSkinType}
        onBack={() => setInlineReportView(null)}
        onViewPdf={(html, filename) => { void openReportAsPdf(html, filename); }}
      />
    );
  }

  return (
    <ProGroupsShell
      initialTab={initialTab}
      apiService={apiService}
      storage={storage}
      clientCacheStorage={clientCacheStorage}
      navigationStateStorage={uiStateStorage}
      onSignOut={onSignOut}
      onViewReport={handleViewReport}
    />
  );
};
